import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Shield, 
  Search, 
  Upload, 
  Download, 
  Loader2, 
  Building2, 
  Globe, 
  FileText,
  History,
  Trash2,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Calendar,
  PieChart as PieChartIcon,
  GitCompare,
  TrendingUp,
  TrendingDown,
  Minus,
  Plus,
  X,
  Bell,
  BellRing,
  Mail,
  Play,
  Power,
  Settings,
  Asterisk,
  Link as LinkIcon,
  ExternalLink,
  Skull,
  User,
  LogOut,
  HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Import auth pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Help from "@/pages/Help";
import Contact from "@/pages/Contact";
import AdminDashboard from "@/pages/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Konami Code Easter Egg
const KONAMI_CODE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

const CHART_COLORS = [
  "hsl(142, 71%, 45%)",
  "hsl(217, 91%, 60%)",
  "hsl(47, 96%, 53%)",
  "hsl(280, 65%, 60%)",
  "hsl(340, 75%, 55%)",
  "hsl(190, 80%, 50%)",
  "hsl(25, 95%, 55%)",
  "hsl(160, 60%, 45%)",
];

const DATE_RANGES = [
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 3 months" },
  { value: "180", label: "Last 6 months" },
  { value: "365", label: "Last year" },
  { value: "custom", label: "Custom range" },
];

const RETRY_OPTIONS = [
  { value: "1", label: "1 attempt" },
  { value: "3", label: "3 attempts" },
  { value: "5", label: "5 attempts" },
  { value: "10", label: "10 attempts" },
];

// Monthly Timeline Chart Component
function MonthlyTimelineChart({ monthlyTimeline }) {
  const [selectedCAs, setSelectedCAs] = useState(new Set());
  
  const timeline = monthlyTimeline?.timeline || [];
  const cas = monthlyTimeline?.cas || [];
  const hasData = timeline.length > 0;
  
  // Initialize selected CAs to top 5 by total certs if not set
  useEffect(() => {
    if (selectedCAs.size === 0 && cas.length > 0 && hasData) {
      // Calculate total certs per CA
      const caTotals = {};
      cas.forEach(ca => {
        caTotals[ca] = timeline.reduce((sum, month) => sum + (month[ca] || 0), 0);
      });
      // Get top 5 CAs
      const topCAs = Object.entries(caTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([ca]) => ca);
      setSelectedCAs(new Set(topCAs));
    }
  }, [cas, timeline, selectedCAs.size, hasData]);

  if (!hasData) {
    return null;
  }

  const toggleCA = (ca) => {
    const newSet = new Set(selectedCAs);
    if (newSet.has(ca)) {
      newSet.delete(ca);
    } else {
      newSet.add(ca);
    }
    setSelectedCAs(newSet);
  };

  const selectAllCAs = () => setSelectedCAs(new Set(cas));
  const clearAllCAs = () => setSelectedCAs(new Set());

  // Filter data to only selected CAs
  const filteredTimeline = timeline.map(month => {
    const filtered = { month: month.month, total: 0 };
    selectedCAs.forEach(ca => {
      if (month[ca]) {
        filtered[ca] = month[ca];
        filtered.total += month[ca];
      }
    });
    return filtered;
  });

  // Get colors for each CA
  const caColors = {};
  Array.from(selectedCAs).forEach((ca, idx) => {
    caColors[ca] = CHART_COLORS[idx % CHART_COLORS.length];
  });

  return (
    <Card className="mt-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Certificate Issuance Timeline by CA
        </CardTitle>
        <CardDescription className="text-xs">
          Monthly breakdown of certificates issued by each Certificate Authority
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* CA Filter */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Select CAs to display:</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={selectAllCAs}>
                All
              </Button>
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={clearAllCAs}>
                Clear
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {cas.map((ca, idx) => (
              <Button
                key={ca}
                variant={selectedCAs.has(ca) ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs gap-1"
                style={selectedCAs.has(ca) ? { backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] } : {}}
                onClick={() => toggleCA(ca)}
              >
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                />
                <span className="truncate max-w-[150px]">{ca}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Bar Chart */}
        {selectedCAs.size > 0 ? (
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={filteredTimeline} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                />
                <Legend 
                  wrapperStyle={{ fontSize: '10px' }}
                  formatter={(value) => <span className="text-xs">{value.length > 25 ? value.slice(0, 25) + '...' : value}</span>}
                />
                {Array.from(selectedCAs).map((ca, idx) => (
                  <Bar 
                    key={ca} 
                    dataKey={ca} 
                    stackId="a"
                    fill={caColors[ca]}
                    name={ca}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
            Select at least one CA to display the chart
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// SAN List View Component with Search and Filter
function SANListView({ sans, caName }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showWildcardsOnly, setShowWildcardsOnly] = useState(false);
  const [copiedSan, setCopiedSan] = useState(null);

  const filteredSans = useMemo(() => {
    let filtered = sans || [];
    
    if (showWildcardsOnly) {
      filtered = filtered.filter(san => san.startsWith("*."));
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(san => san.toLowerCase().includes(term));
    }
    
    return filtered;
  }, [sans, searchTerm, showWildcardsOnly]);

  const wildcardCount = useMemo(() => {
    return (sans || []).filter(san => san.startsWith("*.")).length;
  }, [sans]);

  const copyToClipboard = (san) => {
    navigator.clipboard.writeText(san);
    setCopiedSan(san);
    setTimeout(() => setCopiedSan(null), 1500);
  };

  const copyAllFiltered = () => {
    navigator.clipboard.writeText(filteredSans.join("\n"));
    toast.success(`Copied ${filteredSans.length} SANs to clipboard`);
  };

  if (!sans || sans.length === 0) {
    return (
      <div className="text-center text-xs text-muted-foreground py-4">
        No SANs/FQDNs available for this CA
      </div>
    );
  }

  return (
    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search SANs/FQDNs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-xs"
            data-testid="san-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showWildcardsOnly ? "default" : "outline"}
            size="sm"
            className="h-8 text-xs gap-1"
            onClick={() => setShowWildcardsOnly(!showWildcardsOnly)}
          >
            <Asterisk className="w-3 h-3" />
            Wildcards ({wildcardCount})
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1"
            onClick={copyAllFiltered}
            title="Copy all filtered SANs"
          >
            <Download className="w-3 h-3" />
            Copy ({filteredSans.length})
          </Button>
        </div>
      </div>

      {/* Results Count */}
      <div className="text-xs text-muted-foreground">
        Showing {filteredSans.length} of {sans.length} unique SANs/FQDNs
        {searchTerm && ` matching "${searchTerm}"`}
      </div>

      {/* SAN List */}
      <ScrollArea className="h-[250px] border border-border rounded-lg">
        <div className="p-2 space-y-1">
          {filteredSans.length === 0 ? (
            <div className="text-center text-xs text-muted-foreground py-4">
              No SANs match your filter
            </div>
          ) : (
            filteredSans.map((san, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-muted/50 group text-xs font-mono"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {san.startsWith("*.") ? (
                    <Asterisk className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                  ) : (
                    <Globe className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                  )}
                  <span className="truncate" title={san}>{san}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => copyToClipboard(san)}
                >
                  {copiedSan === san ? (
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                  ) : (
                    <ExternalLink className="w-3 h-3" />
                  )}
                </Button>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// Main Scanner Component
function Scanner({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("domain");
  const [daysBack, setDaysBack] = useState("180");
  const [maxRetries, setMaxRetries] = useState("5");
  const [excludeExpired, setExcludeExpired] = useState(false);
  const [deduplicate, setDeduplicate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [bulkText, setBulkText] = useState("");
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedCAs, setExpandedCAs] = useState(new Set());
  const [activeTab, setActiveTab] = useState("search");
  
  // Custom date range state
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  
  const [filterQuery, setFilterQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortField, setSortField] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showChart, setShowChart] = useState(true);

  const [compareScan1, setCompareScan1] = useState(null);
  const [compareScan2, setCompareScan2] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [isComparing, setIsComparing] = useState(false);

  // Alert state
  const [alerts, setAlerts] = useState([]);
  const [alertLogs, setAlertLogs] = useState([]);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [newAlert, setNewAlert] = useState({
    query: "",
    search_type: "domain",
    email: "",
    alert_on_new_ca: true,
    alert_on_count_change: true,
    count_threshold: 10
  });
  const [checkingAlert, setCheckingAlert] = useState(null);

  // Easter Egg state
  const [hackerMode, setHackerMode] = useState(false);
  const konamiIndex = useRef(0);
  const matrixCanvasRef = useRef(null);

  // Konami Code Easter Egg listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key;
      if (key === KONAMI_CODE[konamiIndex.current]) {
        konamiIndex.current++;
        if (konamiIndex.current === KONAMI_CODE.length) {
          // Easter egg activated!
          setHackerMode(true);
          toast.success("🔓 H4CK3R M0D3 ACTIVATED!", { 
            description: "You found the secret! The Matrix has you...",
            duration: 5000
          });
          konamiIndex.current = 0;
          // Auto-disable after 30 seconds
          setTimeout(() => setHackerMode(false), 30000);
        }
      } else {
        konamiIndex.current = 0;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Matrix rain effect
  useEffect(() => {
    if (!hackerMode || !matrixCanvasRef.current) return;

    const canvas = matrixCanvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*()SSL:CT:LOG:CERT:CA:';
    const charArray = chars.split('');
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    const drops = Array(Math.floor(columns)).fill(1);

    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#22c55e';
      ctx.font = `${fontSize}px JetBrains Mono, monospace`;

      for (let i = 0; i < drops.length; i++) {
        const text = charArray[Math.floor(Math.random() * charArray.length)];
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };

    const interval = setInterval(draw, 35);
    return () => clearInterval(interval);
  }, [hackerMode]);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const [alertsRes, logsRes] = await Promise.all([
        axios.get(`${API}/alerts`),
        axios.get(`${API}/alerts/logs/all`)
      ]);
      setAlerts(alertsRes.data);
      setAlertLogs(logsRes.data);
    } catch (error) {
      console.error("Failed to fetch alerts");
    }
  }, []);

  // Create alert
  const handleCreateAlert = async () => {
    if (!newAlert.query.trim() || !newAlert.email.trim()) {
      toast.error("Please fill in query and email");
      return;
    }

    try {
      const response = await axios.post(`${API}/alerts`, newAlert);
      setAlerts([response.data, ...alerts]);
      setIsAlertModalOpen(false);
      setNewAlert({
        query: "",
        search_type: "domain",
        email: "",
        alert_on_new_ca: true,
        alert_on_count_change: true,
        count_threshold: 10
      });
      toast.success("Alert created successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create alert");
    }
  };

  // Toggle alert
  const toggleAlert = async (alertId) => {
    try {
      const response = await axios.put(`${API}/alerts/${alertId}/toggle`);
      setAlerts(alerts.map(a => 
        a.id === alertId ? { ...a, enabled: response.data.enabled } : a
      ));
      toast.success(response.data.enabled ? "Alert enabled" : "Alert disabled");
    } catch (error) {
      toast.error("Failed to toggle alert");
    }
  };

  // Delete alert
  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`);
      setAlerts(alerts.filter(a => a.id !== alertId));
      toast.success("Alert deleted");
    } catch (error) {
      toast.error("Failed to delete alert");
    }
  };

  // Check alert manually
  const checkAlert = async (alertId) => {
    setCheckingAlert(alertId);
    try {
      const response = await axios.post(`${API}/alerts/${alertId}/check`);
      const { alerts_triggered, is_first_scan, total_certs } = response.data;
      
      if (is_first_scan) {
        toast.success(`Initial scan complete: ${total_certs} certificates found`);
      } else if (alerts_triggered.length > 0) {
        toast.success(`Alert triggered! ${alerts_triggered.length} condition(s) met`);
      } else {
        toast.info(`No changes detected (${total_certs} certificates)`);
      }
      
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check alert");
    } finally {
      setCheckingAlert(null);
    }
  };

  const filteredResults = useMemo(() => {
    let filtered = [...results];
    
    if (filterQuery) {
      filtered = filtered.filter(r => 
        r.query.toLowerCase().includes(filterQuery.toLowerCase())
      );
    }
    
    if (filterStatus === "success") {
      filtered = filtered.filter(r => r.success);
    } else if (filterStatus === "error") {
      filtered = filtered.filter(r => !r.success);
    }
    
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "query":
          aVal = a.query.toLowerCase();
          bVal = b.query.toLowerCase();
          break;
        case "certs":
          aVal = a.total_certs;
          bVal = b.total_certs;
          break;
        case "timestamp":
        default:
          aVal = new Date(a.timestamp).getTime();
          bVal = new Date(b.timestamp).getTime();
      }
      
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return filtered;
  }, [results, filterQuery, filterStatus, sortField, sortDirection]);

  const aggregatedCAData = useMemo(() => {
    const caMap = new Map();
    
    results.forEach(result => {
      if (result.ca_breakdown) {
        result.ca_breakdown.forEach(ca => {
          const existing = caMap.get(ca.ca_name) || 0;
          caMap.set(ca.ca_name, existing + ca.count);
        });
      }
    });
    
    const sorted = Array.from(caMap.entries())
      .map(([name, count]) => ({ name, value: count }))
      .sort((a, b) => b.value - a.value);
    
    if (sorted.length > 8) {
      const top7 = sorted.slice(0, 7);
      const othersCount = sorted.slice(7).reduce((sum, item) => sum + item.value, 0);
      return [...top7, { name: "Others", value: othersCount }];
    }
    
    return sorted;
  }, [results]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const handleScan = async () => {
    if (!user) {
      toast.error("Please sign in to perform scans");
      navigate("/login");
      return;
    }
    
    if (!searchQuery.trim()) {
      toast.error("Please enter a search query");
      return;
    }

    // Validate custom date range if selected
    if (daysBack === "custom" && (!customDateRange.from || !customDateRange.to)) {
      toast.error("Please select both start and end dates");
      return;
    }

    setIsLoading(true);
    try {
      const requestData = {
        query: searchQuery.trim(),
        search_type: searchType,
        max_retries: parseInt(maxRetries),
        exclude_expired: excludeExpired,
        deduplicate: deduplicate,
      };

      // Add date parameters based on selection
      if (daysBack === "custom" && customDateRange.from && customDateRange.to) {
        requestData.start_date = customDateRange.from.toISOString();
        requestData.end_date = customDateRange.to.toISOString();
      } else {
        requestData.days_back = parseInt(daysBack);
      }

      const response = await axios.post(`${API}/scan`, requestData);
      setResults([response.data, ...results]);
      const retryInfo = response.data.retry_attempts > 1 ? ` (${response.data.retry_attempts} attempts)` : "";
      toast.success(`Found ${response.data.total_certs} certificates${retryInfo}`);
      setSearchQuery("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scan CT logs");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkScan = async () => {
    if (!user) {
      toast.error("Please sign in to perform scans");
      navigate("/login");
      return;
    }
    
    const queries = bulkText
      .split("\n")
      .map((q) => q.trim())
      .filter((q) => q.length > 0);

    if (queries.length === 0) {
      toast.error("Please enter at least one query");
      return;
    }

    // Validate custom date range if selected
    if (daysBack === "custom" && (!customDateRange.from || !customDateRange.to)) {
      toast.error("Please select both start and end dates");
      return;
    }

    setIsLoading(true);
    setIsBulkModalOpen(false);
    try {
      const requestData = {
        queries,
        search_type: searchType,
        max_retries: parseInt(maxRetries),
        exclude_expired: excludeExpired,
        deduplicate: deduplicate,
      };

      // Add date parameters based on selection
      if (daysBack === "custom" && customDateRange.from && customDateRange.to) {
        requestData.start_date = customDateRange.from.toISOString();
        requestData.end_date = customDateRange.to.toISOString();
      } else {
        requestData.days_back = parseInt(daysBack);
      }

      const response = await axios.post(`${API}/scan/bulk`, requestData);
      setResults([...response.data, ...results]);
      toast.success(`Scanned ${response.data.length} queries`);
      setBulkText("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk scan failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!user) {
      toast.error("Please sign in to perform scans");
      navigate("/login");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    // Validate custom date range if selected
    if (daysBack === "custom" && (!customDateRange.from || !customDateRange.to)) {
      toast.error("Please select both start and end dates");
      return;
    }

    setIsLoading(true);
    try {
      const parseResponse = await axios.post(`${API}/import/csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const queries = parseResponse.data.queries;
      if (queries.length === 0) {
        toast.error("No valid entries found in CSV");
        return;
      }

      const requestData = {
        queries,
        search_type: searchType,
        max_retries: parseInt(maxRetries),
        exclude_expired: excludeExpired,
        deduplicate: deduplicate,
      };

      // Add date parameters based on selection
      if (daysBack === "custom" && customDateRange.from && customDateRange.to) {
        requestData.start_date = customDateRange.from.toISOString();
        requestData.end_date = customDateRange.to.toISOString();
      } else {
        requestData.days_back = parseInt(daysBack);
      }

      const scanResponse = await axios.post(`${API}/scan/bulk`, requestData);
      setResults([...scanResponse.data, ...results]);
      toast.success(`Imported and scanned ${scanResponse.data.length} entries`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import CSV");
    } finally {
      setIsLoading(false);
      event.target.value = "";
    }
  };

  const handleExport = async () => {
    if (filteredResults.length === 0) {
      toast.error("No results to export");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/export/csv`,
        filteredResults,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ct_log_results.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Results exported successfully");
    } catch (error) {
      toast.error("Failed to export results");
    }
  };

  const fetchHistory = useCallback(async () => {
    if (!user) {
      setHistory([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch history");
      setHistory([]);
    }
  }, [user]);

  const loadFromHistory = async (scanId) => {
    if (!user) {
      toast.error("Please sign in to access history");
      navigate("/login");
      return;
    }
    try {
      const response = await axios.get(`${API}/history/${scanId}`);
      setResults([response.data, ...results.filter((r) => r.id !== scanId)]);
      setActiveTab("search");
      toast.success("Loaded from history");
    } catch (error) {
      toast.error("Failed to load from history");
    }
  };

  const clearResults = () => {
    setResults([]);
    setFilterQuery("");
    setFilterStatus("all");
    toast.success("Results cleared");
  };

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // Toggle CA expansion to show certificates issued by that CA
  const toggleCA = (resultId, caName) => {
    const key = `${resultId}:${caName}`;
    const newExpanded = new Set(expandedCAs);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedCAs(newExpanded);
  };

  // Get certificates issued by a specific CA
  const getCertsByCA = (certificates, caName) => {
    if (!certificates) return [];
    return certificates.filter(cert => cert.issuer === caName);
  };

  const selectForComparison = (scan, slot) => {
    if (slot === 1) {
      setCompareScan1(scan);
    } else {
      setCompareScan2(scan);
    }
    toast.success(`Selected for comparison (Slot ${slot})`);
  };

  const runComparison = async () => {
    if (!compareScan1 || !compareScan2) {
      toast.error("Please select two scans to compare");
      return;
    }

    setIsComparing(true);
    try {
      const response = await axios.post(`${API}/compare`, {
        scan_id_1: compareScan1.id,
        scan_id_2: compareScan2.id,
      });
      setComparisonResult(response.data);
      setActiveTab("compare");
      toast.success("Comparison complete");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Comparison failed");
    } finally {
      setIsComparing(false);
    }
  };

  const clearComparison = () => {
    setCompareScan1(null);
    setCompareScan2(null);
    setComparisonResult(null);
  };

  const totalCerts = results.reduce((sum, r) => sum + r.total_certs, 0);
  const successfulScans = results.filter((r) => r.success).length;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-md p-3 shadow-lg">
          <p className="font-mono text-sm text-foreground">{payload[0].name}</p>
          <p className="font-mono text-primary font-semibold">
            {payload[0].value.toLocaleString()} certs
          </p>
        </div>
      );
    }
    return null;
  };

  const getChangeIndicator = (changeType, change) => {
    switch (changeType) {
      case "new":
        return { icon: <Plus className="w-4 h-4" />, color: "text-green-500", bg: "bg-green-500/10" };
      case "removed":
        return { icon: <X className="w-4 h-4" />, color: "text-red-500", bg: "bg-red-500/10" };
      case "increased":
        return { icon: <TrendingUp className="w-4 h-4" />, color: "text-green-500", bg: "bg-green-500/10" };
      case "decreased":
        return { icon: <TrendingDown className="w-4 h-4" />, color: "text-red-500", bg: "bg-red-500/10" };
      default:
        return { icon: <Minus className="w-4 h-4" />, color: "text-muted-foreground", bg: "bg-muted" };
    }
  };

  return (
    <div className={`app-container ${hackerMode ? 'hacker-mode' : ''}`}>
      {/* Matrix Rain Easter Egg */}
      {hackerMode && (
        <canvas
          ref={matrixCanvasRef}
          className="matrix-canvas"
          onClick={() => setHackerMode(false)}
        />
      )}
      
      <aside className="sidebar">
        <div 
          className="flex items-center gap-3 mb-8 cursor-pointer group"
          onClick={(e) => {
            // Secret: Triple click on logo activates hacker mode
            if (e.detail === 3) {
              setHackerMode(!hackerMode);
              if (!hackerMode) {
                toast.success("🔓 H4CK3R M0D3 ACTIVATED!", { 
                  description: "Click anywhere to exit. Or try: ↑↑↓↓←→←→BA",
                  duration: 5000
                });
              }
            }
          }}
        >
          {hackerMode ? (
            <Skull className="w-8 h-8 text-primary animate-pulse" />
          ) : (
            <Shield className="w-8 h-8 text-primary group-hover:rotate-12 transition-transform" />
          )}
          <span className={`logo-text ${hackerMode ? 'glitch-text' : ''}`}>
            {hackerMode ? 'H4CK3R M0D3' : 'CT Scanner'}
          </span>
        </div>

        <nav className="flex flex-col gap-2">
          <Button
            variant={activeTab === "search" ? "secondary" : "ghost"}
            className="justify-start gap-3"
            onClick={() => setActiveTab("search")}
            data-testid="nav-search"
          >
            <Search className="w-4 h-4" />
            Scanner
          </Button>
          <Button
            variant={activeTab === "history" ? "secondary" : "ghost"}
            className="justify-start gap-3"
            onClick={() => {
              setActiveTab("history");
              fetchHistory();
            }}
            data-testid="nav-history"
          >
            <History className="w-4 h-4" />
            History
          </Button>
          <Button
            variant={activeTab === "compare" ? "secondary" : "ghost"}
            className="justify-start gap-3"
            onClick={() => setActiveTab("compare")}
            data-testid="nav-compare"
          >
            <GitCompare className="w-4 h-4" />
            Compare
            {(compareScan1 || compareScan2) && (
              <Badge variant="secondary" className="ml-auto text-xs">
                {(compareScan1 ? 1 : 0) + (compareScan2 ? 1 : 0)}/2
              </Badge>
            )}
          </Button>
          <Button
            variant={activeTab === "alerts" ? "secondary" : "ghost"}
            className="justify-start gap-3"
            onClick={() => {
              setActiveTab("alerts");
              fetchAlerts();
            }}
            data-testid="nav-alerts"
          >
            <Bell className="w-4 h-4" />
            Alerts
            {alerts.filter(a => a.enabled).length > 0 && (
              <Badge variant="default" className="ml-auto text-xs">
                {alerts.filter(a => a.enabled).length}
              </Badge>
            )}
          </Button>
        </nav>

        <div className="mt-auto pt-8 border-t border-border space-y-3">
          {/* Help Link */}
          <Link to="/help">
            <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground">
              <HelpCircle className="w-4 h-4" />
              Help & FAQ
            </Button>
          </Link>
          
          {/* Contact Link */}
          <Link to="/contact">
            <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground">
              <Mail className="w-4 h-4" />
              Contact Us
            </Button>
          </Link>
          
          {/* User Section */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-3" data-testid="user-menu">
                  <User className="w-4 h-4" />
                  <span className="truncate">{user.name || user.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                {user.is_admin && (
                  <>
                    <Link to="/admin">
                      <DropdownMenuItem className="cursor-pointer" data-testid="admin-link">
                        <Settings className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={onLogout} className="text-red-500 cursor-pointer" data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="space-y-2">
              <Link to="/login">
                <Button variant="outline" className="w-full" data-testid="login-link">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button variant="default" className="w-full" data-testid="register-link">Sign Up</Button>
              </Link>
            </div>
          )}
          
          <p className="text-xs text-muted-foreground">Powered by crt.sh</p>
        </div>
      </aside>

      <main className="main-content">
        {/* Scanner Tab */}
        {activeTab === "search" && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">SSL Certificate Scanner</h1>
              <p className="text-muted-foreground">
                Scan Certificate Transparency logs by organization or domain name
              </p>
            </div>

            {/* Sign In Required Banner */}
            {!user && (
              <Card className="mb-6 border-primary/50 bg-primary/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <Shield className="w-10 h-10 text-primary" />
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">Sign in to start scanning</h3>
                      <p className="text-sm text-muted-foreground">
                        Create a free account to scan CT logs, save your search history, and set up alerts.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Link to="/login">
                        <Button variant="outline">Sign In</Button>
                      </Link>
                      <Link to="/register">
                        <Button>Sign Up Free</Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="mb-6" data-testid="search-card">
              <CardContent className="p-6">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <Select value={searchType} onValueChange={setSearchType} disabled={!user}>
                      <SelectTrigger className="w-full md:w-[180px]" data-testid="search-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="domain">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4" />
                            Domain
                          </div>
                        </SelectItem>
                        <SelectItem value="organization">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            Organization
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={daysBack} onValueChange={(val) => {
                      setDaysBack(val);
                      if (val === "custom") {
                        setIsDatePickerOpen(true);
                      }
                    }} disabled={!user}>
                      <SelectTrigger className="w-full md:w-[180px]" data-testid="date-range-select">
                        <Calendar className="w-4 h-4 mr-2" />
                        <SelectValue>
                          {daysBack === "custom" && customDateRange.from && customDateRange.to
                            ? `${format(customDateRange.from, "MMM d")} - ${format(customDateRange.to, "MMM d")}`
                            : DATE_RANGES.find(r => r.value === daysBack)?.label || "Select range"
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {DATE_RANGES.map(range => (
                          <SelectItem key={range.value} value={range.value}>
                            {range.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Custom Date Range Picker */}
                    {daysBack === "custom" && (
                      <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="gap-2 min-w-[200px]" data-testid="custom-date-picker">
                            <Calendar className="w-4 h-4" />
                            {customDateRange.from && customDateRange.to ? (
                              <span className="text-sm">
                                {format(customDateRange.from, "MMM d, yyyy")} - {format(customDateRange.to, "MMM d, yyyy")}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Pick date range</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="range"
                            selected={customDateRange}
                            onSelect={(range) => {
                              setCustomDateRange(range || { from: null, to: null });
                              if (range?.from && range?.to) {
                                setIsDatePickerOpen(false);
                              }
                            }}
                            disabled={(date) => date > new Date()}
                            numberOfMonths={2}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}

                    <Select value={maxRetries} onValueChange={setMaxRetries} disabled={!user}>
                      <SelectTrigger className="w-full md:w-[140px]" data-testid="retry-select">
                        <Settings className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RETRY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        key={searchType}
                        placeholder={!user ? "Sign in to start scanning" : (searchType === "domain" ? "Enter domain (e.g., google.com)" : "Enter organization name (e.g., Google Inc)")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleScan()}
                        className="pl-10"
                        data-testid="search-input"
                        disabled={!user}
                      />
                    </div>

                    <Button onClick={handleScan} disabled={isLoading || !searchQuery.trim() || !user} className="gap-2" data-testid="scan-button">
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      Scan
                    </Button>

                    <Dialog open={isBulkModalOpen} onOpenChange={setIsBulkModalOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="gap-2" data-testid="bulk-import-button" disabled={!user}>
                          <Upload className="w-4 h-4" />
                          Bulk Import
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Bulk Import</DialogTitle>
                          <DialogDescription>Import multiple queries via text or CSV file upload</DialogDescription>
                        </DialogHeader>
                        <Tabs defaultValue="text" className="mt-4">
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="text"><FileText className="w-4 h-4 mr-2" />Text</TabsTrigger>
                            <TabsTrigger value="csv"><Upload className="w-4 h-4 mr-2" />CSV</TabsTrigger>
                          </TabsList>
                          <TabsContent value="text" className="mt-4">
                            <Textarea placeholder="Enter one query per line..." value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={8} className="font-mono text-sm" />
                            <Button className="w-full mt-4" onClick={handleBulkScan} disabled={!bulkText.trim()}>Scan All</Button>
                          </TabsContent>
                          <TabsContent value="csv" className="mt-4">
                            <div className="upload-zone relative">
                              <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                              <p className="text-sm text-muted-foreground mb-2">Drop CSV file here or click to browse</p>
                              <input type="file" accept=".csv" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                              <Button variant="outline" size="sm">Select File</Button>
                            </div>
                          </TabsContent>
                        </Tabs>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {/* Filter Options */}
                  <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-border/50">
                    <span className="text-xs text-muted-foreground">Filters:</span>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="exclude-expired"
                        checked={excludeExpired}
                        onCheckedChange={setExcludeExpired}
                        disabled={!user}
                        data-testid="exclude-expired-switch"
                      />
                      <Label htmlFor="exclude-expired" className="text-sm cursor-pointer">
                        Exclude expired certificates
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="deduplicate"
                        checked={deduplicate}
                        onCheckedChange={setDeduplicate}
                        disabled={!user}
                        data-testid="deduplicate-switch"
                      />
                      <Label htmlFor="deduplicate" className="text-sm cursor-pointer">
                        Deduplicate (pre)certificate pairs
                      </Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {results.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-1 space-y-4">
                  <Card><CardContent className="p-6"><div className="stat-value">{results.length}</div><div className="stat-label">Queries Scanned</div></CardContent></Card>
                  <Card><CardContent className="p-6"><div className="stat-value text-primary">{totalCerts.toLocaleString()}</div><div className="stat-label">Total Certificates</div></CardContent></Card>
                  <Card><CardContent className="p-6"><div className="stat-value text-green-500">{successfulScans}</div><div className="stat-label">Successful Scans</div></CardContent></Card>
                </div>
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2"><PieChartIcon className="w-5 h-5 text-primary" />CA Distribution</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => setShowChart(!showChart)}>{showChart ? "Hide" : "Show"}</Button>
                    </div>
                  </CardHeader>
                  {showChart && aggregatedCAData.length > 0 && (
                    <CardContent>
                      <div className="h-[280px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={aggregatedCAData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""} labelLine={false}>
                              {aggregatedCAData.map((entry, index) => (<Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} stroke="hsl(var(--background))" strokeWidth={2} />))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ maxWidth: "45%", fontSize: "11px" }} formatter={(value) => (<span className="text-xs text-muted-foreground" title={value}>{value.length > 35 ? value.substring(0, 35) + "..." : value}</span>)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  )}
                </Card>
              </div>
            )}

            {results.length > 0 && (
              <Card className="mb-4">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="flex flex-1 gap-3 items-center w-full md:w-auto">
                      <div className="relative flex-1 md:max-w-xs">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input placeholder="Filter by query..." value={filterQuery} onChange={(e) => setFilterQuery(e.target.value)} className="pl-10" />
                      </div>
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="success">Success Only</SelectItem>
                          <SelectItem value="error">Errors Only</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-muted-foreground whitespace-nowrap">{filteredResults.length} of {results.length} results</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleExport} className="gap-2"><Download className="w-4 h-4" />Export CSV</Button>
                      <Button variant="ghost" size="sm" onClick={clearResults} className="gap-2 text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" />Clear</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {isLoading && results.length === 0 ? (
              <Card><CardContent className="loading-overlay"><div className="loading-spinner" /><p className="text-muted-foreground font-mono">Scanning CT logs...</p></CardContent></Card>
            ) : results.length === 0 ? (
              <Card><CardContent className="empty-state"><Shield className="empty-state-icon" /><h3 className="text-lg font-semibold mb-2">No Results Yet</h3><p className="text-sm">Enter a domain or organization name to scan CT logs</p></CardContent></Card>
            ) : (
              <Card>
                <ScrollArea className="h-[500px]">
                  <table className="results-table">
                    <thead>
                      <tr>
                        <th className="w-8"></th>
                        <th className="cursor-pointer" onClick={() => handleSort("query")}><div className="flex items-center">Query {getSortIcon("query")}</div></th>
                        <th>Type</th>
                        <th className="cursor-pointer" onClick={() => handleSort("certs")}><div className="flex items-center">Certificates {getSortIcon("certs")}</div></th>
                        <th>Status</th>
                        <th className="cursor-pointer" onClick={() => handleSort("timestamp")}><div className="flex items-center">Scanned {getSortIcon("timestamp")}</div></th>
                        <th>Compare</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredResults.map((result) => (
                        <React.Fragment key={result.id}>
                          <tr className="cursor-pointer" onClick={() => toggleRow(result.id)}>
                            <td>{expandedRows.has(result.id) ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}</td>
                            <td className="font-mono">{result.query}</td>
                            <td><Badge variant="secondary" className="gap-1">{result.search_type === "domain" ? <Globe className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}{result.search_type}</Badge></td>
                            <td><span className="font-mono text-primary font-semibold">{result.total_certs.toLocaleString()}</span></td>
                            <td>
                              <div className="flex items-center gap-2">
                                {result.success ? <Badge className="badge-success gap-1"><CheckCircle2 className="w-3 h-3" />Success</Badge> : <Badge className="badge-error gap-1"><AlertCircle className="w-3 h-3" />Error</Badge>}
                                {result.retry_attempts > 1 && (
                                  <span className="text-xs text-muted-foreground" title={`Required ${result.retry_attempts} attempts`}>
                                    ({result.retry_attempts}x)
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-muted-foreground text-xs">{new Date(result.timestamp).toLocaleString()}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div className="flex gap-1">
                                <Button variant={compareScan1?.id === result.id ? "default" : "ghost"} size="sm" className="h-7 px-2 text-xs" onClick={() => selectForComparison(result, 1)}>A</Button>
                                <Button variant={compareScan2?.id === result.id ? "default" : "ghost"} size="sm" className="h-7 px-2 text-xs" onClick={() => selectForComparison(result, 2)}>B</Button>
                              </div>
                            </td>
                          </tr>
                          {expandedRows.has(result.id) && (
                            <tr>
                              <td colSpan={7} className="bg-secondary/30 p-4">
                                {result.error_message ? (
                                  <div className="text-destructive text-sm"><AlertCircle className="w-4 h-4 inline mr-2" />{result.error_message}</div>
                                ) : (
                                  <div className="space-y-6">
                                    {/* Summary Cards Row */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                      {/* Validation Type Summary */}
                                      {result.validation_summary && (
                                        <>
                                          <div className="bg-card rounded-lg p-3 border border-border">
                                            <div className="flex items-center gap-2 mb-2">
                                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                              <span className="text-xs text-muted-foreground">DV Certificates</span>
                                            </div>
                                            <div className="font-mono text-xl font-bold">{result.validation_summary.dv_count}</div>
                                            <div className="text-xs text-muted-foreground">{result.validation_summary.dv_percentage}%</div>
                                          </div>
                                          <div className="bg-card rounded-lg p-3 border border-border">
                                            <div className="flex items-center gap-2 mb-2">
                                              <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                              <span className="text-xs text-muted-foreground">OV Certificates</span>
                                            </div>
                                            <div className="font-mono text-xl font-bold">{result.validation_summary.ov_count}</div>
                                            <div className="text-xs text-muted-foreground">{result.validation_summary.ov_percentage}%</div>
                                          </div>
                                          <div className="bg-card rounded-lg p-3 border border-border">
                                            <div className="flex items-center gap-2 mb-2">
                                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                              <span className="text-xs text-muted-foreground">EV Certificates</span>
                                            </div>
                                            <div className="font-mono text-xl font-bold">{result.validation_summary.ev_count}</div>
                                            <div className="text-xs text-muted-foreground">{result.validation_summary.ev_percentage}%</div>
                                          </div>
                                        </>
                                      )}
                                      {/* SAN Summary */}
                                      {result.san_summary && (
                                        <div className="bg-card rounded-lg p-3 border border-border">
                                          <div className="flex items-center gap-2 mb-2">
                                            <LinkIcon className="w-3 h-3 text-primary" />
                                            <span className="text-xs text-muted-foreground">SANs / Wildcards</span>
                                          </div>
                                          <div className="font-mono text-xl font-bold">{result.san_summary.unique_sans}</div>
                                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                                            {result.san_summary.wildcard_sans > 0 && (
                                              <span className="text-yellow-500 flex items-center gap-1">
                                                <Asterisk className="w-3 h-3" />{result.san_summary.wildcard_sans} wildcards
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Wildcard Domains */}
                                    {result.san_summary && result.san_summary.wildcard_domains && result.san_summary.wildcard_domains.length > 0 && (
                                      <div>
                                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                                          <Asterisk className="w-4 h-4 text-yellow-500" />
                                          Wildcard Certificates
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                          {result.san_summary.wildcard_domains.map((wc, idx) => (
                                            <Badge key={idx} variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30 font-mono text-xs">
                                              {wc}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Top SANs */}
                                    {result.san_summary && result.san_summary.top_sans && result.san_summary.top_sans.length > 0 && (
                                      <div>
                                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                                          <Globe className="w-4 h-4 text-primary" />
                                          Top FQDNs/SANs
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          {result.san_summary.top_sans.slice(0, 6).map((san, idx) => (
                                            <div key={idx} className="flex items-center justify-between bg-card rounded px-3 py-2 border border-border">
                                              <span className="font-mono text-xs truncate flex items-center gap-2">
                                                {san.is_wildcard && <Asterisk className="w-3 h-3 text-yellow-500 flex-shrink-0" />}
                                                {san.san}
                                              </span>
                                              <Badge variant="secondary" className="ml-2 font-mono text-xs">{san.count}</Badge>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* CA Breakdown - Expandable */}
                                    {result.ca_breakdown.length > 0 && (
                                      <div>
                                        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                          <Building2 className="w-4 h-4" />
                                          Certificates by Issuing CA
                                          <span className="text-xs text-muted-foreground font-normal">(click to expand)</span>
                                        </h4>
                                        <div className="space-y-2">
                                          {result.ca_breakdown.map((ca, idx) => {
                                            const caKey = `${result.id}:${ca.ca_name}`;
                                            const isExpanded = expandedCAs.has(caKey);
                                            const caCerts = getCertsByCA(result.certificates, ca.ca_name);
                                            
                                            return (
                                              <div key={`${result.id}-ca-${idx}`} className="border border-border rounded-lg overflow-hidden">
                                                <div 
                                                  className="ca-item cursor-pointer hover:bg-muted/50 transition-colors"
                                                  onClick={() => toggleCA(result.id, ca.ca_name)}
                                                >
                                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    {isExpanded ? (
                                                      <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                    ) : (
                                                      <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                    )}
                                                    <span className="ca-name">{ca.ca_name}</span>
                                                  </div>
                                                  <div className="flex items-center gap-3 flex-shrink-0">
                                                    <div className="flex items-center gap-1 text-xs text-muted-foreground" title="SANs / Unique SANs">
                                                      <LinkIcon className="w-3 h-3" />
                                                      <span className="text-primary font-medium">{ca.san_count || 0}</span>
                                                      <span>/</span>
                                                      <span>{ca.unique_san_count || 0}</span>
                                                      {(ca.wildcard_count || 0) > 0 && (
                                                        <span className="text-yellow-500 flex items-center gap-0.5">
                                                          <Asterisk className="w-3 h-3" />{ca.wildcard_count}
                                                        </span>
                                                      )}
                                                    </div>
                                                    <Badge variant="secondary" className="ca-count">
                                                      {ca.count} cert{ca.count !== 1 ? 's' : ''}
                                                    </Badge>
                                                  </div>
                                                </div>
                                                
                                                {isExpanded && caCerts.length > 0 && (
                                                  <div className="bg-card/50 border-t border-border p-3">
                                                    {/* Tabs for Certificates and SANs */}
                                                    <div className="flex gap-2 mb-3">
                                                      <Button
                                                        variant={!expandedCAs.has(`${caKey}:sans`) ? "default" : "outline"}
                                                        size="sm"
                                                        className="text-xs h-7"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const newSet = new Set(expandedCAs);
                                                          newSet.delete(`${caKey}:sans`);
                                                          setExpandedCAs(newSet);
                                                        }}
                                                      >
                                                        Certificates ({caCerts.length})
                                                      </Button>
                                                      <Button
                                                        variant={expandedCAs.has(`${caKey}:sans`) ? "default" : "outline"}
                                                        size="sm"
                                                        className="text-xs h-7"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          const newSet = new Set(expandedCAs);
                                                          newSet.add(`${caKey}:sans`);
                                                          setExpandedCAs(newSet);
                                                        }}
                                                      >
                                                        <LinkIcon className="w-3 h-3 mr-1" />
                                                        SANs/FQDNs ({ca.unique_san_count || 0})
                                                      </Button>
                                                    </div>
                                                    
                                                    {/* Certificates Table */}
                                                    {!expandedCAs.has(`${caKey}:sans`) && (
                                                      <div className="overflow-x-auto">
                                                        <table className="w-full text-xs">
                                                          <thead>
                                                            <tr className="border-b border-border/50">
                                                              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Common Name</th>
                                                              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Type</th>
                                                              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">SANs</th>
                                                              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Valid From</th>
                                                              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Valid To</th>
                                                            </tr>
                                                          </thead>
                                                          <tbody>
                                                            {caCerts.map((cert, certIdx) => (
                                                              <tr key={certIdx} className="border-b border-border/30 hover:bg-muted/20">
                                                                <td className="py-1.5 px-2 font-mono">
                                                                  <div className="flex items-center gap-1">
                                                                    {cert.is_wildcard && <Asterisk className="w-3 h-3 text-yellow-500 flex-shrink-0" />}
                                                                    <span className="truncate max-w-[180px]" title={cert.common_name}>{cert.common_name}</span>
                                                                  </div>
                                                                </td>
                                                                <td className="py-1.5 px-2">
                                                                  <Badge 
                                                                    variant="outline" 
                                                                    className={`text-xs ${
                                                                      cert.validation_type === 'EV' ? 'bg-green-500/10 text-green-500 border-green-500/30' :
                                                                      cert.validation_type === 'OV' ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30' :
                                                                      'bg-blue-500/10 text-blue-500 border-blue-500/30'
                                                                    }`}
                                                                  >
                                                                    {cert.validation_type}
                                                                  </Badge>
                                                                </td>
                                                                <td className="py-1.5 px-2 font-mono">
                                                                  <span className="text-primary">{cert.san_count}</span>
                                                                  {cert.wildcard_count > 0 && (
                                                                    <span className="text-yellow-500 ml-1">({cert.wildcard_count}*)</span>
                                                                  )}
                                                                </td>
                                                                <td className="py-1.5 px-2 text-muted-foreground whitespace-nowrap">
                                                                  {cert.not_before ? new Date(cert.not_before).toLocaleDateString() : '-'}
                                                                </td>
                                                                <td className="py-1.5 px-2 text-muted-foreground whitespace-nowrap">
                                                                  {cert.not_after ? new Date(cert.not_after).toLocaleDateString() : '-'}
                                                                </td>
                                                              </tr>
                                                            ))}
                                                          </tbody>
                                                        </table>
                                                      </div>
                                                    )}
                                                    
                                                    {/* SANs List with Search */}
                                                    {expandedCAs.has(`${caKey}:sans`) && ca.sans && (
                                                      <SANListView sans={ca.sans} caName={ca.ca_name} />
                                                    )}
                                                    
                                                    {caCerts.length < ca.count && !expandedCAs.has(`${caKey}:sans`) && (
                                                      <p className="text-xs text-muted-foreground mt-2 text-center">
                                                        Showing {caCerts.length} of {ca.count} certificates from this CA
                                                      </p>
                                                    )}
                                                  </div>
                                                )}
                                                
                                                {isExpanded && caCerts.length === 0 && (
                                                  <div className="bg-card/50 border-t border-border p-3 text-center text-xs text-muted-foreground">
                                                    Certificate details not available (data truncated)
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    {result.ca_breakdown.length === 0 && (
                                      <p className="text-muted-foreground text-sm">No certificates found</p>
                                    )}

                                    {/* Monthly Timeline Chart */}
                                    {result.monthly_timeline && result.monthly_timeline.timeline && result.monthly_timeline.timeline.length > 0 && (
                                      <MonthlyTimelineChart monthlyTimeline={result.monthly_timeline} />
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </Card>
            )}
          </>
        )}

        {/* History Tab */}
        {activeTab === "history" && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">Scan History</h1>
              <p className="text-muted-foreground">View and reload previous scan results</p>
            </div>

            {history.length === 0 ? (
              <Card><CardContent className="empty-state"><History className="empty-state-icon" /><h3 className="text-lg font-semibold mb-2">No History</h3><p className="text-sm">Your scan history will appear here</p></CardContent></Card>
            ) : (
              <Card>
                <ScrollArea className="h-[600px]">
                  <table className="results-table">
                    <thead><tr><th>Query</th><th>Type</th><th>Certificates</th><th>Scanned</th><th>Actions</th></tr></thead>
                    <tbody>
                      {history.map((item) => (
                        <tr key={item.id}>
                          <td className="font-mono">{item.query}</td>
                          <td><Badge variant="secondary">{item.search_type}</Badge></td>
                          <td className="font-mono text-primary">{item.total_certs.toLocaleString()}</td>
                          <td className="text-muted-foreground text-xs">{new Date(item.timestamp).toLocaleString()}</td>
                          <td>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => loadFromHistory(item.id)}>Load</Button>
                              <Button variant={compareScan1?.id === item.id ? "default" : "outline"} size="sm" className="h-7 px-2 text-xs" onClick={() => selectForComparison(item, 1)}>A</Button>
                              <Button variant={compareScan2?.id === item.id ? "default" : "outline"} size="sm" className="h-7 px-2 text-xs" onClick={() => selectForComparison(item, 2)}>B</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </Card>
            )}
          </>
        )}

        {/* Compare Tab */}
        {activeTab === "compare" && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">Compare Scans</h1>
              <p className="text-muted-foreground">Track certificate changes between two scan results</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Card className={compareScan1 ? "border-primary/50" : ""}>
                <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2"><Badge variant="secondary">A</Badge>Older Scan</CardTitle></CardHeader>
                <CardContent>
                  {compareScan1 ? (
                    <div className="space-y-2">
                      <p className="font-mono text-lg">{compareScan1.query}</p>
                      <p className="text-sm text-muted-foreground">{compareScan1.total_certs} certificates</p>
                      <p className="text-xs text-muted-foreground">{new Date(compareScan1.timestamp).toLocaleString()}</p>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setCompareScan1(null)}><X className="w-4 h-4 mr-1" /> Remove</Button>
                    </div>
                  ) : (<p className="text-muted-foreground text-sm">Select a scan (click "A" button)</p>)}
                </CardContent>
              </Card>
              <Card className={compareScan2 ? "border-primary/50" : ""}>
                <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2"><Badge variant="secondary">B</Badge>Newer Scan</CardTitle></CardHeader>
                <CardContent>
                  {compareScan2 ? (
                    <div className="space-y-2">
                      <p className="font-mono text-lg">{compareScan2.query}</p>
                      <p className="text-sm text-muted-foreground">{compareScan2.total_certs} certificates</p>
                      <p className="text-xs text-muted-foreground">{new Date(compareScan2.timestamp).toLocaleString()}</p>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setCompareScan2(null)}><X className="w-4 h-4 mr-1" /> Remove</Button>
                    </div>
                  ) : (<p className="text-muted-foreground text-sm">Select a scan (click "B" button)</p>)}
                </CardContent>
              </Card>
            </div>

            <div className="flex gap-4 mb-6">
              <Button onClick={runComparison} disabled={!compareScan1 || !compareScan2 || isComparing} className="gap-2">
                {isComparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />}Compare Scans
              </Button>
              {(compareScan1 || compareScan2 || comparisonResult) && (<Button variant="outline" onClick={clearComparison}>Clear Selection</Button>)}
            </div>

            {comparisonResult && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <Card><CardContent className="p-4"><div className="text-2xl font-bold font-mono">{comparisonResult.scan_1.total_certs}</div><div className="text-xs text-muted-foreground">Scan A Certs</div></CardContent></Card>
                  <Card><CardContent className="p-4"><div className="text-2xl font-bold font-mono">{comparisonResult.scan_2.total_certs}</div><div className="text-xs text-muted-foreground">Scan B Certs</div></CardContent></Card>
                  <Card><CardContent className="p-4"><div className={`text-2xl font-bold font-mono ${comparisonResult.total_change > 0 ? "text-green-500" : comparisonResult.total_change < 0 ? "text-red-500" : ""}`}>{comparisonResult.total_change > 0 ? "+" : ""}{comparisonResult.total_change}</div><div className="text-xs text-muted-foreground">Net Change</div></CardContent></Card>
                  <Card><CardContent className="p-4"><div className="text-2xl font-bold font-mono text-primary">{comparisonResult.new_cas.length} / {comparisonResult.removed_cas.length}</div><div className="text-xs text-muted-foreground">New / Removed CAs</div></CardContent></Card>
                </div>

                {comparisonResult.new_cas.length > 0 && (
                  <Card className="mb-4 border-green-500/30">
                    <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2 text-green-500"><Plus className="w-5 h-5" />New CAs ({comparisonResult.new_cas.length})</CardTitle></CardHeader>
                    <CardContent><div className="flex flex-wrap gap-2">{comparisonResult.new_cas.map((ca, idx) => (<Badge key={idx} className="badge-success">{ca}</Badge>))}</div></CardContent>
                  </Card>
                )}

                {comparisonResult.removed_cas.length > 0 && (
                  <Card className="mb-4 border-red-500/30">
                    <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2 text-red-500"><X className="w-5 h-5" />Removed CAs ({comparisonResult.removed_cas.length})</CardTitle></CardHeader>
                    <CardContent><div className="flex flex-wrap gap-2">{comparisonResult.removed_cas.map((ca, idx) => (<Badge key={idx} className="badge-error">{ca}</Badge>))}</div></CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-lg">Detailed CA Changes</CardTitle></CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      <table className="results-table">
                        <thead><tr><th>Certificate Authority</th><th>Scan A</th><th>Scan B</th><th>Change</th><th>Status</th></tr></thead>
                        <tbody>
                          {comparisonResult.ca_changes.map((change, idx) => {
                            const indicator = getChangeIndicator(change.change_type, change.change);
                            return (
                              <tr key={idx}>
                                <td className="font-mono text-sm">{change.ca_name}</td>
                                <td className="font-mono">{change.old_count}</td>
                                <td className="font-mono">{change.new_count}</td>
                                <td className={`font-mono font-semibold ${indicator.color}`}>{change.change > 0 ? "+" : ""}{change.change}</td>
                                <td><Badge className={`gap-1 ${indicator.bg} ${indicator.color} border-0`}>{indicator.icon}{change.change_type}</Badge></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </>
            )}

            {!comparisonResult && !compareScan1 && !compareScan2 && (
              <Card><CardContent className="empty-state"><GitCompare className="empty-state-icon" /><h3 className="text-lg font-semibold mb-2">No Comparison Selected</h3><p className="text-sm text-center max-w-md">Select two scans using A/B buttons</p></CardContent></Card>
            )}
          </>
        )}

        {/* Alerts Tab */}
        {activeTab === "alerts" && (
          <>
            <div className="flex justify-between items-start mb-8">
              <div>
                <h1 className="text-3xl font-bold mb-2">Certificate Alerts</h1>
                <p className="text-muted-foreground">Get notified when new CAs issue certificates or counts change</p>
              </div>
              <Dialog open={isAlertModalOpen} onOpenChange={setIsAlertModalOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="create-alert-button">
                    <Plus className="w-4 h-4" />
                    Create Alert
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create Alert</DialogTitle>
                    <DialogDescription>Set up monitoring for a domain or organization</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Search Type</Label>
                        <Select value={newAlert.search_type} onValueChange={(v) => setNewAlert({...newAlert, search_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="domain">Domain</SelectItem>
                            <SelectItem value="organization">Organization</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Query</Label>
                        <Input placeholder="e.g., example.com" value={newAlert.query} onChange={(e) => setNewAlert({...newAlert, query: e.target.value})} data-testid="alert-query-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Email for Notifications</Label>
                      <Input type="email" placeholder="your@email.com" value={newAlert.email} onChange={(e) => setNewAlert({...newAlert, email: e.target.value})} data-testid="alert-email-input" />
                    </div>
                    <div className="space-y-4 pt-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Alert on New CA</Label>
                          <p className="text-xs text-muted-foreground">When a new CA starts issuing certificates</p>
                        </div>
                        <Switch checked={newAlert.alert_on_new_ca} onCheckedChange={(v) => setNewAlert({...newAlert, alert_on_new_ca: v})} />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Alert on Count Change</Label>
                          <p className="text-xs text-muted-foreground">When certificate count changes significantly</p>
                        </div>
                        <Switch checked={newAlert.alert_on_count_change} onCheckedChange={(v) => setNewAlert({...newAlert, alert_on_count_change: v})} />
                      </div>
                      {newAlert.alert_on_count_change && (
                        <div className="space-y-2">
                          <Label>Change Threshold</Label>
                          <Input type="number" min={1} value={newAlert.count_threshold} onChange={(e) => setNewAlert({...newAlert, count_threshold: parseInt(e.target.value) || 10})} />
                          <p className="text-xs text-muted-foreground">Alert when count changes by this amount or more</p>
                        </div>
                      )}
                    </div>
                    <Button className="w-full" onClick={handleCreateAlert} data-testid="save-alert-button">Create Alert</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Active Alerts */}
            <div className="grid gap-4 mb-8">
              {alerts.length === 0 ? (
                <Card>
                  <CardContent className="empty-state">
                    <Bell className="empty-state-icon" />
                    <h3 className="text-lg font-semibold mb-2">No Alerts</h3>
                    <p className="text-sm">Create an alert to monitor certificate changes</p>
                  </CardContent>
                </Card>
              ) : (
                alerts.map((alert) => (
                  <Card key={alert.id} className={alert.enabled ? "border-primary/30" : "opacity-60"} data-testid={`alert-card-${alert.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-full ${alert.enabled ? "bg-primary/10" : "bg-muted"}`}>
                            {alert.enabled ? <BellRing className="w-5 h-5 text-primary" /> : <Bell className="w-5 h-5 text-muted-foreground" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold">{alert.query}</span>
                              <Badge variant="secondary">{alert.search_type}</Badge>
                              {!alert.enabled && <Badge variant="outline">Disabled</Badge>}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                              <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{alert.email}</span>
                              {alert.alert_on_new_ca && <span>New CA alerts</span>}
                              {alert.alert_on_count_change && <span>Count ±{alert.count_threshold}</span>}
                              {alert.last_checked && <span>Last: {new Date(alert.last_checked).toLocaleString()}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => checkAlert(alert.id)} disabled={checkingAlert === alert.id || !alert.enabled} className="gap-1" data-testid={`check-alert-${alert.id}`}>
                            {checkingAlert === alert.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            Check Now
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => toggleAlert(alert.id)} data-testid={`toggle-alert-${alert.id}`}>
                            <Power className={`w-4 h-4 ${alert.enabled ? "text-green-500" : "text-muted-foreground"}`} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteAlert(alert.id)} className="text-destructive hover:text-destructive" data-testid={`delete-alert-${alert.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Alert Logs */}
            {alertLogs.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Recent Alert Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {alertLogs.map((log) => (
                        <div key={log.id} className="flex items-start gap-3 p-3 bg-secondary/30 rounded-lg">
                          <div className={`p-1.5 rounded-full ${log.alert_type === "new_ca" ? "bg-green-500/10" : "bg-blue-500/10"}`}>
                            {log.alert_type === "new_ca" ? <Plus className="w-4 h-4 text-green-500" /> : <TrendingUp className="w-4 h-4 text-blue-500" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm">{log.query}</span>
                              <Badge variant="outline" className="text-xs">{log.alert_type === "new_ca" ? "New CA" : "Count Change"}</Badge>
                              {log.email_sent ? (
                                <Badge className="badge-success text-xs gap-1"><Mail className="w-3 h-3" />Sent</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">No email</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{log.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">{new Date(log.timestamp).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// Main App Component with Routing
function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const navigate = useNavigate();

  // Load user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setToken(null);
    toast.success("Signed out successfully");
    navigate("/");
  };

  return (
    <>
      <Toaster position="top-right" richColors theme="dark" />
      <Routes>
        <Route path="/" element={<Scanner user={user} onLogout={handleLogout} />} />
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/register" element={<Register onLogin={handleLogin} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/help" element={<Help />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/admin" element={<AdminDashboard user={user} />} />
      </Routes>
    </>
  );
}

export default App;
